import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  SxProps,
  Theme,
  TextField,
  Button,
  Avatar,
  Skeleton,
  Card,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import UmmIAIcon from "./svg/UmmIAAgentIcon2.svg";
import { useChatUI } from "./ChatUIContext";
import ReactMarkdown from "react-markdown";
import { ReplayCircleFilledRounded } from "@mui/icons-material";
import MarkDownText from "../MarkDownText/MarkDownText";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { useLocation } from "react-router-dom";
import { getChatSession, getChatsSessionsByProyectoId } from "../../hooks/editor/useChatSession";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";




export type UmmIAChatModalProps = {
  open: boolean;
  onClose: () => void;
  mode?: "floating" | "inline";
  styleOverrides?: SxProps<Theme>;
  onCollapse?: () => void;
};

type ChatRole = "user" | "assistant";

export type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: number;
  contextSnapshot?: string;
};

const MessageBubble: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  const isUser = msg.role === "user";
  const { applyDoc } = useChatUI();

  const [openConfirm, setOpenConfirm] = useState(false);
  const handleAskConfirm = () => setOpenConfirm(true);
  const handleCancel = () => setOpenConfirm(false);
  const handleApply = () => {
    if (msg.contextSnapshot) applyDoc(msg.contextSnapshot);
    setOpenConfirm(false);
  };

  const [openResp, setOpenResp] = useState(false);
  const handleCloseResp = () => setOpenResp(false);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          justifyContent: isUser ? "flex-end" : "flex-start",
          mb: 1.25,
        }}
        aria-label={isUser ? "Mensaje del usuario" : "Mensaje del asistente"}
      >
        {!isUser && (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              mt: 0.25,
              bgcolor: "#DAE4EC",
              color: "#000",
              fontSize: "0.75rem",
              fontFamily: "Montserrat",
              flexShrink: 0,
            }}
            aria-hidden
          >
            UA
          </Avatar>
        )}

        <Box
          sx={{
            maxWidth: { xs: "100%", sm: "80%" },
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: isUser ? "rgba(249, 186, 50, 0.3)" : "#DAE4EC",
            border: "1px solid rgba(0,0,0,0.06)",
            fontFamily: "Montserrat",
            fontSize: "12px",
            color: "#333",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {msg.content}

          {/* Card "Volver atrás" solo para mensajes del asistente que tengan contextSnapshot */}
          {!isUser && msg.contextSnapshot && (
            <Card
              onClick={handleAskConfirm}
              sx={{
                p: 1.2,
                mt: 1,
                bgcolor: "rgba(255, 255, 255, 0.6)",
                borderRadius: 5,
                fontFamily: "Montserrat",
                border: "1px solid rgba(0,0,0,0.06)",
                position: "relative",
                cursor: "pointer",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover .hover-blur": { opacity: 1 },
                "&:hover .back-text": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              }}
              aria-label={
                isUser ? "Volver al contexto" : "Aplicar cambio propuesto"
              }
              title={
                isUser
                  ? "Volver atrás (restaurar en el editor)"
                  : "Aplicar en el editor"
              }
            >
              <Box
                sx={{
                  fontSize: "8px",
                  lineHeight: 1.2,
                  maxHeight: "45px",
                  overflow: "hidden",
                  pr: 1,
                  "& h1, & h2, & h3, & p, & li": {
                    fontSize: "9px",
                    fontWeight: 400,
                    margin: 0,
                  },
                }}
              >
                <ReactMarkdown>{msg.contextSnapshot ?? ""}</ReactMarkdown>
              </Box>

              <Box
                className="hover-blur"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(255,255,255,0.3)",
                  backdropFilter: "blur(1px)",
                  borderRadius: 2,
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                  pointerEvents: "none",
                }}
              />
              <Typography
                className="back-text"
                sx={{
                  position: "absolute",
                  bottom: 4,
                  left: "30%",
                  transform: "translate(-50%, 150px)",
                  opacity: 0,
                  transition: "all 0.3s ease",
                  color: "#336B87",
                  fontWeight: 600,
                  fontSize: "12px",
                  fontFamily: "Montserrat",
                  pointerEvents: "none",
                }}
              >
                Volver atrás
              </Typography>
            </Card>
          )}
        </Box>

        {isUser && (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              mt: 0.25,
              bgcolor: "rgba(249, 186, 50, 0.3)",
              color: "#000",
              fontSize: "0.75rem",
              fontFamily: "Montserrat",
              flexShrink: 0,
            }}
            aria-hidden
          >
            Tú
          </Avatar>
        )}
      </Box>

      {/* Modal de respuesta */}
      <Dialog
        open={openResp}
        onClose={handleCloseResp}
        BackdropProps={{ sx: { backgroundColor: "rgba(29, 61, 77, 0.6)" } }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: 5,
            padding: 0,
            minWidth: 360,
            maxWidth: 680,
            fontFamily: "'Montserrat', sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: "bold",
            color: "#336B87",
            fontSize: 18,
            px: 3,
            pt: 3,
          }}
        >
          Respuesta del asistente
        </DialogTitle>
        <DialogContent sx={{ fontFamily: "Montserrat" }}>
          <Box
            sx={{
              maxHeight: 360,
              overflow: "auto",
              p: 1,
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.02)",
              fontSize: 13,
            }}
          >
            <MarkDownText data={msg.content} img={null} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "center" }}>
          <Button
            variant="text"
            onClick={() => {
              navigator.clipboard?.writeText(msg.content).catch(() => { });
              handleCloseResp();
            }}
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#336B87",
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#336B87",
                color: "#FFFFFF",
              },
            }}
          >
            Copiar y cerrar
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseResp}
            sx={{
              backgroundColor: "#336B87",
              color: "#FFFFFF",
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { backgroundColor: "#1FC16B", color: "#FFFFFF" },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal aplicar cambio */}
      <Dialog
        open={openConfirm}
        onClose={handleCancel}
        BackdropProps={{ sx: { backgroundColor: "rgba(29, 61, 77, 0.6)" } }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: 5,
            padding: 0,
            minWidth: 320,
            fontFamily: "'Montserrat', sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: "bold",
            color: "#336B87",
            fontSize: 18,
            px: 3,
            pt: 3,
          }}
        >
          {isUser ? "¿Restaurar respuesta anterior?" : "¿Aplicar cambio propuesto?"}
        </DialogTitle>

        <DialogContent sx={{ fontFamily: "Montserrat" }}>
          <Typography
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 14,
              textAlign: "center",
            }}
          >
            {isUser
              ? "Se reemplazará el contenido de tu respuesta por el siguiente:"
              : "Se reemplazará el contenido del editor por el siguiente:"}
          </Typography>

          <Box
            sx={{
              maxHeight: 260,
              overflow: "auto",
              p: 1,
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.02)",
              fontSize: 12,
            }}
          >
            <MarkDownText data={msg.contextSnapshot ?? ""} img={null} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "center" }}>
          <Button
            onClick={handleCancel}
            variant="text"
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#FB3748",
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { backgroundColor: "#FB3748", color: "#FFFFFF" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: "#336B87",
              color: "#FFFFFF",
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              ml: 1,
              "&:hover": { backgroundColor: "#1FC16B", color: "#FFFFFF" },
            }}
            autoFocus
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const UmmIAChatModal: React.FC<UmmIAChatModalProps> = ({
  open,
  onClose,
  mode = "floating",
  onCollapse,
  styleOverrides,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState<boolean>(false);
  const { idProyecto } = useActividadesContext();

 
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  const { askInstruction, getContextSnapshot, setLoadingResponse, applyDoc } =
    useChatUI();
  const handleCollapse = onCollapse ?? onClose;

  const location = useLocation();

  const proyectoFromLocation = (location.state as any)?.proyecto;

  const proyecto = proyectoFromLocation ?? idProyecto ?? null;


  const bodyRef = useRef<HTMLDivElement | null>(null);

  const originalDocRef = useRef<string | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const showQuickActions = useMemo(
    () =>
      !isLoadingHistory &&
      messages.length === 0 &&
      inputValue.trim().length === 0,
    [isLoadingHistory, messages.length, inputValue]
  );

 
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // Efecto para cargar historial cuando se abra el modal y haya proyectoId
  useEffect(() => {
    const loadHistory = async () => {
      if (!open) return;
      if (!proyecto?.proyectoId) return;

      try {
        setIsLoadingHistory(true);

        // Buscar sesiones del proyecto
        const sessions: any[] =
          (await getChatsSessionsByProyectoId(proyecto.proyectoId)) ?? [];

        if (!sessions.length) {
          setMessages([]);
          return;
        }

        // Tomar la sesión más reciente (por updatedAt o createdAt)
        const sortedSessions = [...sessions].sort((a, b) => {
          const aTime = new Date(a.updatedAt ?? a.createdAt).getTime();
          const bTime = new Date(b.updatedAt ?? b.createdAt).getTime();
          return aTime - bTime;
        });

        const lastSession = sortedSessions[sortedSessions.length - 1];

        // Obtener mensajes de esa sesión
        const dbMessages: any[] = (await getChatSession(lastSession.id)) ?? [];

        // Ordenar mensajes por fecha ascendente
        const sortedMessages = [...dbMessages].sort((a, b) => {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return aTime - bTime;
        });

        // Mapear al tipo ChatMessage respetando actor / instruction / content
        const mapped: ChatMessage[] = sortedMessages.map((m) => {
          const created = new Date(m.createdAt).getTime();

          if (m.actor === "assistant") {
            return {
              id: m.id,
              role: "assistant",
              content: m.instruction ?? "",
              contextSnapshot: m.content ?? "",
              createdAt: created,
            };
          }

          return {
            id: m.id,
            role: "user",
            content: m.instruction ?? m.content ?? "",
            createdAt: created,
          };
        });

        setMessages(mapped);

        // Inicializar respuesta original
        const firstAssistantWithContext = mapped.find(
          (msg) => msg.role === "assistant" && !!msg.contextSnapshot
        );
        if (firstAssistantWithContext) {
          originalDocRef.current = firstAssistantWithContext.contextSnapshot!;
        }
      } catch (error) {
        console.error("Error cargando historial de chat:", error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [open, proyecto?.proyectoId]);

  const handleSend = useCallback(async () => {
  const trimmed = inputValue.trim();
  if (!trimmed) return;

  const snapshot = getContextSnapshot();

  if (!originalDocRef.current && snapshot) {
    originalDocRef.current = snapshot;
  }

  const userMsg: ChatMessage = {
    id: `user-${Date.now()}`,
    role: "user",
    content: trimmed,
    createdAt: Date.now(),
    contextSnapshot: snapshot,
  };

  setMessages((prev) => [...prev, userMsg]);
  setInputValue("");
  setIsSending(true);

  try {
    setLoadingResponse(true);
    const { assistantText, contextSnapshot } = await askInstruction(trimmed);

    const assistantMsg: ChatMessage = {
      id: `asst-${Date.now()}`,
      role: "assistant",
      content: assistantText,
      createdAt: Date.now(),
      contextSnapshot: contextSnapshot,
    };

    setMessages((prev) => [...prev, assistantMsg]);

     if (!originalDocRef.current && contextSnapshot) {
      originalDocRef.current = contextSnapshot;
    }

    if (contextSnapshot) {
      console.log(
        "[UmmIAChatModal] Auto-aplicando contextSnapshot en el editor..."
      );
      applyDoc(contextSnapshot);
    }
  } catch (err) {
    console.error(err);
    setMessages((prev) => [
      ...prev,
      {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: "Ocurrió un error procesando tu instrucción.",
        createdAt: Date.now(),
      },
    ]);
  } finally {
    setIsSending(false);
    setLoadingResponse(false);
  }
}, [inputValue, askInstruction, getContextSnapshot, setLoadingResponse, applyDoc]);


  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (!open) return null;

  const handleOpenRestoreDialog = () => {
    if (!originalDocRef.current) return;
    setRestoreDialogOpen(true);
  };

  const handleApplyOriginal = () => {
    if (originalDocRef.current) {
      applyDoc(originalDocRef.current);
    }
    setRestoreDialogOpen(false);
  };

  return (
    <Box
      sx={{
        bgcolor: "#DAE4EC33",
        boxShadow: 6,
        borderRadius: "20px",
        border: "1px solid #CCCCCC",
        overflow: "hidden",
        zIndex: 99,
        ...(mode === "inline"
          ? {
            position: "relative",
            width: "100%",
            height: "100%",
            minHeight: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }
          : {
            position: "fixed",
            right: 24,
            bottom: 24,
            width: { xs: "calc(100% - 48px)", sm: 360 },
            height: { xs: "70vh", sm: "80vh" },
          }),
        ...styleOverrides,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="UmmIA Chat Modal"
      data-testid="ummia-chat-modal"
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: 1,
          flexShrink: 0,
          backgroundColor: "rgba(218, 228, 236, 0.2)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src={UmmIAIcon} alt="UmmIA AI" width={36} height={36} />
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "flex-end",
              lineHeight: 1,
              pt: 3,
              ml: -0.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "28px",
                fontFamily: "Montserrat",
                color: "#336B87",
                lineHeight: 1.05,
              }}
            >
              UmmIA Agent
            </Typography>

            <Typography
              component="span"
              sx={{
                mt: "-8px",
                fontFamily: "Montserrat",
                fontWeight: 900,
                fontStyle: "italic",
                letterSpacing: "0.5px",
                fontSize: "16px",
                color: "#336B87",
              }}
            >
              BETA
            </Typography>
          </Box>
        </Box>
        <Tooltip
          title="Contraer UmmIA Agent"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#1D3D4D",
                color: "#FFF",
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                padding: "8px 12px",
                borderRadius: "8px",
              },
            },
            arrow: { sx: { color: "#1D3D4D" } },
          }}
        >
          <IconButton
            size="small"
            onClick={handleCollapse}
            aria-label="Contraer UmmIA Agent"
            sx={{
              borderRadius: 2,
              bgcolor: "transparent",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            <ChevronLeftRoundedIcon sx={{ color: "#336B87" }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Créditos
      <CreditosUmmIAAgent /> */}

      {/* Volver a la respuesta original */}
      {originalDocRef.current && (
        <Box sx={{ px: 2, pb: 0.5, bgcolor: "#DAE4EC33" }}>
          <Chip
            icon={<ReplayCircleFilledRounded fontSize="small" />}
            label="Volver a la respuesta original"
            size="small"
            onClick={handleOpenRestoreDialog}
            sx={{
              fontFamily: "Montserrat",
              fontSize: "11px",
              borderRadius: 2,
              bgcolor: "#FFFFFF",
              color: "#336B87",
              "& .MuiChip-icon": { color: "#336B87" },
              "&:hover": { bgcolor: "#E4EEF4" },
            }}
          />
        </Box>
      )}

      {/* Body */}
      <Box
        ref={bodyRef}
        sx={{
          p: 2,
          overflowY: "auto",
          flex: 1,
          background: "#DAE4EC33",
        }}
      >
        {isLoadingHistory && messages.length === 0 ? (
          // Skeleton mientras carga el historial
          <Box>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "12px",
                color: "#7e7e7eff",
                mb: 1,
              }}
            >
              Cargando historial de conversación…
            </Typography>
            <Skeleton animation="wave" height={20} sx={{ mb: 1 }} />
            <Skeleton animation="wave" height={20} width="80%" />
          </Box>
        ) : showQuickActions ? (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "#7e7e7eff",
                mb: 0.3,
                fontFamily: "Montserrat",
                fontSize: "13px",
              }}
            >
              ¿Cómo te puedo ayudar?
            </Typography>
          </>
        ) : (
          <>
            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}

            {messages.length === 0 && !isLoadingHistory && (
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "11px",
                  color: "text.secondary",
                }}
              >
                Tu conversación aparecerá aquí…
              </Typography>
            )}

            {isSending && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    mt: 0.25,
                    bgcolor: "rgba(249, 186, 50, 0.3)",
                    color: "#fff",
                    fontSize: "0.75rem",
                    fontFamily: "Montserrat",
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  U
                </Avatar>
                <Box sx={{ width: 160 }}>
                  <Skeleton animation="wave" height={10} sx={{ mb: 0.5 }} />
                  <Skeleton animation="wave" height={10} width="70%" />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Input + botón */}
      <Box
        sx={{
          p: 1,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          borderRadius: "15px",
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0,
          backgroundColor: "#F3F3F3",
        }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          <TextField
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Introduce una petición o modificación aquí..."
            aria-label="Área para escribir tu mensaje"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                fontFamily: "Montserrat",
                fontSize: "12px",
                backgroundColor: "#FCFCFC",
              },
            }}
          />

          <Button
            variant="contained"
            disabled={!inputValue.trim() || isSending}
            onClick={handleSend}
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              borderRadius: "20px",
              fontSize: "9px",
              fontFamily: "Montserrat",
              bgcolor: "#52A381",
              "&:hover": { bgcolor: "#46906a" },
              "&.Mui-disabled": {
                bgcolor: "#BFCFC3",
                color: "#fff",
              },
            }}
            aria-label="Enviar mensaje"
          >
            GENERAR
          </Button>
        </Box>
      </Box>

      {/* Modal para restaurar respuesta original */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        BackdropProps={{ sx: { backgroundColor: "rgba(29, 61, 77, 0.6)" } }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: 5,
            padding: 0,
            minWidth: 320,
            maxWidth: 680,
            fontFamily: "'Montserrat', sans-serif",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: "bold",
            color: "#336B87",
            fontSize: 18,
            px: 3,
            pt: 3,
          }}
        >
          ¿Restaurar respuesta original?
        </DialogTitle>

        <DialogContent sx={{ fontFamily: "Montserrat" }}>
          <Typography
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 14,
              textAlign: "center",
              mb: 1,
            }}
          >
            Se reemplazará el contenido actual del editor por la respuesta original:
          </Typography>

          <Box
            sx={{
              maxHeight: 260,
              overflow: "auto",
              p: 1,
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.02)",
              fontSize: 12,
            }}
          >
            <MarkDownText data={originalDocRef.current ?? ""} img={null} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "center" }}>
          <Button
            onClick={() => setRestoreDialogOpen(false)}
            variant="text"
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#FB3748",
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { backgroundColor: "#FB3748", color: "#FFFFFF" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApplyOriginal}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: "#336B87",
              color: "#FFFFFF",
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              borderRadius: 2,
              ml: 1,
              "&:hover": { backgroundColor: "#1FC16B", color: "#FFFFFF" },
            }}
            autoFocus
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UmmIAChatModal;
